// src/pages/Consulting.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import StandardPage from '../components/Layout/StandardPage';
import {
  Calendar as AntCalendar,
  message,
  Segmented,
  Table,
  Space,
  Select,
  DatePicker,
  Button,
  Typography,
  Card,
  Badge,
  Input,
  Collapse,
  Drawer,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { 
  PlusOutlined, 
  ReloadOutlined, 
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import type { ActivityDTO } from '../services/activities';
import type { ColumnsType } from 'antd/es/table';
import CreateActivityDrawer from '../components/activities/CreateActivityDrawer';
import { listActivities, listAllActivities } from '../services/activities';
import { listCustomers, type CustomerDTO } from '../services/customers';
import { getTeam, type UserDTO } from '../services/users';

dayjs.locale('it');
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

type ViewMode = 'day' | 'week' | 'month' | 'table';

// Status color mapping with muted tones
const STATUS_COLORS = {
  confirmed: { bg: '#f0f9f0', border: '#d9f7be', text: '#52c41a' },
  pending: { bg: '#fafafa', border: '#e8e8e8', text: '#8c8c8c' },
  cancelled: { bg: '#fff2f0', border: '#ffccc7', text: '#ff4d4f' },
} as const;

const hexToRgba = (hex: string, alpha: number) => {
  const clean = hex.replace('#', '').trim();
  if (clean.length !== 6) return `rgba(82,196,26,${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// DTO helpers shared across views
const getStart = (a: ActivityDTO) => a.appointment?.startAt ?? a.occurredAt;
const getEnd = (a: ActivityDTO) => a.appointment?.endAt ?? null;
const isAllDay = (a: ActivityDTO) => !!a.appointment?.allDay;
const getTitle = (a: ActivityDTO) => a.appointment?.title || a.name || 'Consulenza';
const getCreatorLabel = (a: ActivityDTO) => {
  const full = `${a.userFirstName ?? ''} ${a.userLastName ?? ''}`.trim();
  return full || a.userEmail || (a.userId != null ? `#${a.userId}` : '—');
};
const isMediaShoot = (a: ActivityDTO) =>
  a.type === 'CONSULTANCY' || a.activityType?.code === 'CONSULTANCY';

const getCardColors = (a: ActivityDTO, creatorColorById: Map<number, string>) => {
  const creatorId = a.userId ?? null;
  const userColor = creatorId != null ? creatorColorById.get(creatorId) : null;
  if (userColor) {
    return {
      bg: hexToRgba(userColor, 0.12),
      border: hexToRgba(userColor, 0.35),
      text: userColor,
    };
  }
  return STATUS_COLORS.confirmed;
};

const ConsultingPage: React.FC = () => {
  const [value, setValue] = useState<Dayjs>(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ActivityDTO[]>([]);
  const [tableItems, setTableItems] = useState<ActivityDTO[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(8);

  // Drawer create/edit
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<number | undefined>(undefined);

  const [presetStart, setPresetStart] = useState<string | undefined>(undefined);
  const [presetEnd, setPresetEnd] = useState<string | undefined>(undefined);


  const [dayDrawerOpen, setDayDrawerOpen] = useState(false);
  const [drawerDate, setDrawerDate] = useState<Dayjs | null>(null);
  const [tblDateRange, setTblDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('week'),
    dayjs().endOf('week'),
  ]);
  const [tblCustomer, setTblCustomer] = useState<string | undefined>(undefined);
  const [tblCreator, setTblCreator] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>('');

  // Customers
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [team, setTeam] = useState<UserDTO[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const list = await listCustomers();
        setCustomers(Array.isArray(list) ? list : []);
      } catch {
        // Silent fail
      }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const list = await getTeam();
        setTeam(Array.isArray(list) ? list : []);
      } catch {
        // Silent fail
      }
    })();
  }, []);

  const activeCustomerIds = useMemo(
    () => new Set((customers || []).filter(c => c.isActive).map(c => c.id)),
    [customers]
  );
  const creatorColorById = useMemo(() => {
    const map = new Map<number, string>();
    team.forEach(u => {
      if (u?.id != null && u.color) {
        map.set(u.id, u.color);
      }
    });
    return map;
  }, [team]);

  // Visible range calculation
  const visibleRange = useMemo(() => {
    let start: Dayjs;
    let end: Dayjs;

    if (viewMode === 'day') {
      start = value.startOf('day');
      end = value.endOf('day');
      return { startMs: start.valueOf(), endMs: end.valueOf(), start, end };
    }
    if (viewMode === 'week') {
      start = value.startOf('week');
      end = value.endOf('week');
      return { startMs: start.valueOf(), endMs: end.valueOf(), start, end };
    }
    start = value.startOf('month').startOf('week');
    end = value.endOf('month').endOf('week');
    return { startMs: start.valueOf(), endMs: end.valueOf(), start, end };
  }, [value, viewMode]);

  const fetchCalendarItems = useCallback(async () => {
    setLoading(true);
    try {
      const from = visibleRange.start.toISOString();
      const to = visibleRange.end.toISOString();
      const all = await listAllActivities({
        from,
        to,
        type: 'CONSULTANCY',
        sort: 'occurredAt,asc',
        size: 500,
        maxPages: 50,
      });
      const onlyShoots = all.filter(a => isMediaShoot(a));
      onlyShoots.sort((a, b) => dayjs(getStart(a)).valueOf() - dayjs(getStart(b)).valueOf());
      setItems(onlyShoots);
    } catch (e: any) {
      message.error(e?.message || 'Impossibile caricare le attività');
    } finally {
      setLoading(false);
    }
  }, [visibleRange.start, visibleRange.end]);

  const fetchTableItems = useCallback(async () => {
    if (!tblDateRange?.[0] || !tblDateRange?.[1]) return;
    setLoading(true);
    try {
      const from = tblDateRange[0].startOf('day').toISOString();
      const to = tblDateRange[1].endOf('day').toISOString();
      const page = await listActivities({
        page: tablePage - 1,
        size: tablePageSize,
        sort: 'occurredAt,asc',
        from,
        to,
        type: 'CONSULTANCY',
      });
      const onlyShoots = (page.content || []).filter(a => isMediaShoot(a));
      onlyShoots.sort((a, b) => dayjs(getStart(a)).valueOf() - dayjs(getStart(b)).valueOf());
      setTableItems(onlyShoots);
      setTableTotal(page.totalElements ?? onlyShoots.length);
    } catch (e: any) {
      message.error(e?.message || 'Impossibile caricare le attività');
    } finally {
      setLoading(false);
    }
  }, [tblDateRange, tablePage, tablePageSize]);

  useEffect(() => {
    if (viewMode !== 'table') {
      fetchCalendarItems();
    }
  }, [fetchCalendarItems, viewMode]);

  useEffect(() => {
    if (viewMode === 'table') {
      fetchTableItems();
    }
  }, [fetchTableItems, viewMode]);

  useEffect(() => {
    setTablePage(1);
  }, [tblDateRange]);

  const refreshData = useCallback(() => {
    if (viewMode === 'table') {
      return fetchTableItems();
    }
    return fetchCalendarItems();
  }, [fetchCalendarItems, fetchTableItems, viewMode]);

  // Group by day
  const byDay = useMemo(() => {
    const map: Record<string, ActivityDTO[]> = {};
    for (const a of items) {
      const key = dayjs(getStart(a)).format('YYYY-MM-DD');
      (map[key] ||= []).push(a);
    }
    Object.values(map).forEach(list =>
      list.sort((x, y) => dayjs(getStart(x)).valueOf() - dayjs(getStart(y)).valueOf())
    );
    return map;
  }, [items]);

  const selectedDayKey = useMemo(
    () => (drawerDate ? drawerDate.format('YYYY-MM-DD') : null),
    [drawerDate]
  );

  const selectedDayEvents = useMemo(
    () => (selectedDayKey ? byDay[selectedDayKey] || [] : []),
    [selectedDayKey, byDay]
  );

  // Create helpers
  const openCreateForDate = (date: Dayjs) => {
    const start = date.hour(10).minute(0).second(0).millisecond(0);
    const end = start.add(2, 'hour');
    setPresetStart(start.toISOString());
    setPresetEnd(end.toISOString());
    setCreateOpen(true);
  };

  const openDayDrawer = (date: Dayjs) => {
    setValue(date);
    setDrawerDate(date.startOf('day'));
    setDayDrawerOpen(true);
  };

  const closeDayDrawer = () => {
    setDayDrawerOpen(false);
    setDrawerDate(null);
  };

  const openCreateNow = () => {
    const base = value || dayjs();
    openCreateForDate(base);
  };

  const openEdit = (id: number) => {
    setEditId(id);
    setEditOpen(true);
  };

  // Enhanced date cell render for month view
  const dateCellRender = (date: Dayjs) => {
    const key = date.format('YYYY-MM-DD');
    const dayEvents = byDay[key] || [];
    const isToday = date.isSame(dayjs(), 'day');
    
    if (!dayEvents.length) {
      return (
        <div
          onClick={(e) => {
            e.stopPropagation();
            openDayDrawer(date);
          }}
          style={{ 
            width: '100%', 
            height: '100%', 
            minHeight: 60,
            cursor: 'pointer',
            position: 'relative',
            borderRadius: 4,
            transition: 'background-color 0.2s ease',
          }}
          className="empty-cell"
        />
      );
    }

    const visible = dayEvents.slice(0, 2);
    const more = dayEvents.length - visible.length;

    return (
      <div
        style={{ 
          padding: '4px',
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2,
          height: '100%',
          minHeight: 60,
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.stopPropagation();
          openDayDrawer(date);
        }}
      >
        {visible.map(a => {
          const colors = getCardColors(a, creatorColorById);
          const start = getStart(a);
          const end = getEnd(a);
          const creatorLabel = getCreatorLabel(a);

          const timeLabel = isAllDay(a)
            ? 'Tutto il giorno'
            : end
              ? `${dayjs(start).format('HH:mm')}–${dayjs(end).format('HH:mm')}`
              : dayjs(start).format('HH:mm');

          return (
            <div
              key={a.id}
              style={{
                borderRadius: 4,
                padding: '3px 6px',
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                cursor: 'pointer',
                fontSize: 11,
                lineHeight: 1.2,
                transition: 'all 0.2s ease',
              }}
              title={`${getTitle(a)} - ${timeLabel}${a.customerName ? ` - ${a.customerName}` : ''}`}
              onClick={(ev) => {
                ev.stopPropagation();
                openEdit(a.id);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ 
                fontWeight: 500, 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                color: colors.text,
              }}>
                {getTitle(a)}
              </div>
              {!isAllDay(a) && (
                <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 1 }}>
                  {timeLabel}
                </div>
              )}
              {creatorLabel && (
                <div style={{ fontSize: 10, color: '#595959', marginTop: 1 }}>
                  <UserOutlined style={{ marginRight: 4 }} />
                  {creatorLabel}
                </div>
              )}
              {a.customerName && (
                <div style={{ fontSize: 10, color: '#595959', marginTop: 1 }}>
                  <UserOutlined style={{ marginRight: 4 }} />
                  {a.customerName}
                </div>
              )}
            </div>
          );
        })}
        {more > 0 && (
          <div style={{ 
            fontSize: 10, 
            color: '#8c8c8c', 
            textAlign: 'center',
            padding: '2px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
            onClick={(e) => {
              e.stopPropagation();
              openDayDrawer(date);
            }}
          >
            +{more} altri
          </div>
        )}
      </div>
    );
  };

  // Enhanced week view
  const weekDays = useMemo(() => {
    const start = visibleRange.start.startOf('week');
    return Array.from({ length: 7 }).map((_, i) => start.add(i, 'day'));
  }, [visibleRange.start]);

  const renderEventCard = (a: ActivityDTO) => {
    const colors = getCardColors(a, creatorColorById);
    const start = getStart(a);
    const end = getEnd(a);
    const timeLabel = isAllDay(a)
      ? 'Tutto il giorno'
      : end
        ? `${dayjs(start).format('HH:mm')}–${dayjs(end).format('HH:mm')}`
        : dayjs(start).format('HH:mm');

    return (
      <Card
        key={a.id}
        size="small"
        hoverable
        onClick={(ev) => { ev.stopPropagation(); openEdit(a.id); }}
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          marginBottom: 8,
          transition: 'all 0.2s ease',
        }}
        bodyStyle={{ padding: '8px 12px' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: 600, 
              fontSize: 13,
              color: colors.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginBottom: 2,
            }}>
              {getTitle(a)}
            </div>
            <Space size={1} direction="vertical">
              <Text type="secondary" style={{ fontSize: 12 }}>
                <ClockCircleOutlined style={{ marginRight: 2 }} />
                {timeLabel}
              </Text>
              {a.customerName && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <UserOutlined style={{ marginRight: 2 }} />
                  {a.customerName}
                  
                </Text>
              )}
            <Space>
              <Text type="secondary">
                <UserOutlined style={{ marginRight: 4 }} />
                {getCreatorLabel(a)}
              </Text>
            </Space>
            </Space>
          </div>
        </div>
      </Card>
    );
  };

  const WeekView: React.FC = () => {
    return (
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: 16,
        marginTop: 16,
      }}>
        {weekDays.map(d => {
          const key = d.format('YYYY-MM-DD');
          const events = (byDay[key] || []);
          const isToday = d.isSame(dayjs(), 'day');
          const isWeekend = d.day() === 0 || d.day() === 6;
          
          return (
            <Card
              key={key}
              size="small"
              style={{
                background: isToday ? '#f0f9ff' : isWeekend ? '#fafafa' : '#ffffff',
                border: isToday ? '1px solid #91caff' : '1px solid #f0f0f0',
                borderRadius: 8,
                minHeight: 200,
                transition: 'all 0.2s ease',
              }}
              bodyStyle={{ padding: '12px' }}
              hoverable
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 12,
                borderBottom: '1px solid #f0f0f0',
                paddingBottom: 8,
              }}>
                <div>
                  <Text strong style={{ 
                    fontSize: 14, 
                    color: isToday ? '#1890ff' : '#262626',
                  }}>
                    {d.format('ddd')}
                  </Text>
                  <div style={{ 
                    fontSize: 18, 
                    fontWeight: isToday ? 700 : 600,
                    color: isToday ? '#1890ff' : '#262626',
                    lineHeight: 1,
                  }}>
                    {d.format('DD')}
                  </div>
                </div>
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    openCreateForDate(d); 
                  }}
                  style={{ 
                    color: '#8c8c8c',
                    fontSize: 12,
                  }}
                />
              </div>
              
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {events.length ? (
                  events.map(renderEventCard)
                ) : (
                  <div
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      openCreateForDate(d); 
                    }}
                    style={{
                      textAlign: 'center',
                      padding: '20px 0',
                      color: '#bfbfbf',
                      fontSize: 12,
                      cursor: 'pointer',
                      borderRadius: 4,
                      border: '2px dashed #f0f0f0',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#d9d9d9';
                      e.currentTarget.style.background = '#fafafa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#f0f0f0';
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    Nessun evento
                    <br />
                    <PlusOutlined style={{ marginTop: 4 }} />
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  const DayView: React.FC = () => {
    const d = value.startOf('day');
    const key = d.format('YYYY-MM-DD');
    const events = byDay[key] || [];
    const isToday = d.isSame(dayjs(), 'day');
    
    return (
      <Card
        style={{
          background: isToday ? '#f0f9ff' : '#ffffff',
          border: isToday ? '1px solid #91caff' : '1px solid #f0f0f0',
          borderRadius: 12,
          marginTop: 16,
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <div>
            <Title level={3} style={{ 
              margin: 0, 
              color: isToday ? '#1890ff' : '#262626',
            }}>
              {d.format('dddd DD MMMM YYYY')}
            </Title>
            <Text type="secondary">
              {events.length} {events.length === 1 ? 'appuntamento' : 'appuntamenti'} programmati
            </Text>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={(e) => { 
              e.stopPropagation(); 
              openCreateForDate(d); 
            }}
            style={{ borderRadius: 8 }}
          >
            Nuova consulenza
          </Button>
        </div>
        
        <div style={{ display: 'grid', gap: 12 }}>
          {events.length ? (
            events.map(renderEventCard)
          ) : (
            <div
              onClick={(e) => { 
                e.stopPropagation(); 
                openCreateForDate(d); 
              }}
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#bfbfbf',
                fontSize: 16,
                cursor: 'pointer',
                borderRadius: 8,
                border: '2px dashed #f0f0f0',
                background: '#fafafa',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#d9d9d9';
                e.currentTarget.style.background = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#f0f0f0';
                e.currentTarget.style.background = '#fafafa';
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <CalendarOutlined style={{ fontSize: 32, color: '#d9d9d9' }} />
              </div>
              Nessun evento per oggi
              <div style={{ fontSize: 14, marginTop: 8 }}>
                Clicca per aggiungere una nuova consulenza
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  // Table filters and data
  const customerOptions = useMemo(() => {
    const map = new Map<number, string>();
    tableItems.forEach(a => {
      if (a.customerId != null && activeCustomerIds.has(a.customerId)) {
        map.set(a.customerId, a.customerName || `#${a.customerId}`);
      }
    });

    const entries: [number, string][] = [];
    map.forEach((label, id) => entries.push([id, label]));

    return entries
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([, label]) => ({ label, value: label }));
  }, [tableItems, activeCustomerIds]);

  const allCreatorLabels = useMemo(() => {
    const set = new Set<string>();
    tableItems.forEach(a => {
      const label = getCreatorLabel(a);
      if (label) set.add(label);
    });
    return Array.from(set).sort();
  }, [tableItems]);

  const tableData = useMemo(() => {
    return tableItems.filter(a => {
      // Customer filter
      if (tblCustomer) {
        const label = a.customerName || (a.customerId != null ? `#${a.customerId}` : '—');
        if (label !== tblCustomer) return false;
      }
      
      // Creator filter
      if (tblCreator) {
        const label = getCreatorLabel(a);
        if (label !== tblCreator) return false;
      }
      
      // Search filter
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const title = getTitle(a).toLowerCase();
        const customer = (a.customerName || '').toLowerCase();
        const creator = getCreatorLabel(a).toLowerCase();
        
        if (!title.includes(searchLower) && 
            !customer.includes(searchLower) && 
            !creator.includes(searchLower)) {
          return false;
        }
      }

      return true;
    });
  }, [tableItems, tblCustomer, tblCreator, searchText]);

  const columns: ColumnsType<ActivityDTO> = useMemo(() => ([
    {
      title: 'Titolo',
      key: 'title',
      render: (_: any, a) => {
        return (
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {getTitle(a)}
          </div>
        );
      },
      sorter: (a, b) => getTitle(a).localeCompare(getTitle(b)),
    },
    {
      title: 'Data e ora',
      key: 'datetime',
      render: (_: any, a) => {
        const start = getStart(a);
        const end = getEnd(a);
        
        return (
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>
              {dayjs(start).format('DD/MM/YYYY')}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {isAllDay(a) 
                ? 'Tutto il giorno'
                : end
                  ? `${dayjs(start).format('HH:mm')} - ${dayjs(end).format('HH:mm')}`
                  : dayjs(start).format('HH:mm')
              }
            </Text>
          </div>
        );
      },
      sorter: (a, b) => dayjs(getStart(a)).valueOf() - dayjs(getStart(b)).valueOf(),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Cliente',
      key: 'customer',
      render: (_: any, a) => (
        <Space size={8}>
          <UserOutlined style={{ color: '#8c8c8c' }} />
          <span>{a.customerName || (a.customerId != null ? `#${a.customerId}` : '—')}</span>
        </Space>
      ),
    },
    {
      title: 'Utente',
      key: 'creator',
      render: (_: any, a) => {
        const fullName = `${a.userFirstName ?? ''} ${a.userLastName ?? ''}`.trim();
        return (
          <Space direction="vertical" size={0}>
            <Space size={6}>
              <UserOutlined style={{ color: '#8c8c8c' }} />
              <Text style={{ fontSize: 13 }}>
                {fullName || '-'}
              </Text>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {a.userEmail || '-'}
            </Text>
          </Space>
        );
      },
      sorter: (a, b) => {
        const na = `${a.userFirstName ?? ''} ${a.userLastName ?? ''}`.trim();
        const nb = `${b.userFirstName ?? ''} ${b.userLastName ?? ''}`.trim();
        return na.localeCompare(nb);
      },
    },
  ]), []);

  return (
    <StandardPage
      title="Calendario consulenze"
      subtitle="Gestione consulenze"
      loading={loading}
      actions={[
        {
          label: 'Nuova consulenza',
          icon: <PlusOutlined />,
          onClick: openCreateNow,
          type: 'primary',
        },
        {
          label: 'Aggiorna',
          icon: <ReloadOutlined />,
          onClick: refreshData,
          type: 'default',
        },
      ]}
    >
      {/* Enhanced view selector */}
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Segmented
              value={viewMode}
              size="middle"
              onChange={(val) => setViewMode(val as ViewMode)}
              options={[
                { label: 'Giorno', value: 'day', icon: <CalendarOutlined /> },
                { label: 'Settimana', value: 'week', icon: <CalendarOutlined /> },
                { label: 'Mese', value: 'month', icon: <CalendarOutlined /> },
                { label: 'Tabella', value: 'table', icon: <FilterOutlined /> },
              ]}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {viewMode === 'table' ? tableTotal : items.length} eventi trovati
            </Text>
           
          </div>
        </div>
      </Card>

      {/* Calendar Views */}
      {viewMode === 'month' && (
        <Card style={{ borderRadius: 12 }}>
          <AntCalendar
            value={value}
            mode="month"
            onChange={(v) => setValue(v)}
            onPanelChange={(v) => setValue(v)}
            cellRender={(date, info) => {
              if (info.type === 'date') {
                return (
                  <div style={{ 
                    width: '100%', 
                    minHeight: 80, 
                    padding: 4, 
                    boxSizing: 'border-box',
                    position: 'relative',
                  }}>
                    {dateCellRender(date)}
                  </div>
                );
              }
              return <div style={{ width: '100%', height: '100%' }} />;
            }}

          />
        </Card>
      )}

      {viewMode === 'week' && <WeekView />}

      {viewMode === 'day' && <DayView />}

      {/* Enhanced Table View */}
      {viewMode === 'table' && (
        <>
          {/* Collapsible Filters */}
          <Collapse
            size="small"
            style={{ marginBottom: 16 }}
            items={[
              {
                key: 'filters',
                label: (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FilterOutlined />
                    <span>Filtri avanzati</span>
                    <Badge count={[tblCustomer, tblCreator, searchText].filter(Boolean).length} />
                  </div>
                ),
                extra: (
                  <Button
                    size="small"
                    type="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTblDateRange([dayjs().startOf('week'), dayjs().endOf('week')]);
                      setTblCustomer(undefined);
                      setTblCreator(undefined);
                      setSearchText('');
                    }}
                  >
                    Reset
                  </Button>
                ),
                children: (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 16,
                    alignItems: 'end',
                    padding: '8px 0',
                  }}>
                    {/* Search */}
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        Ricerca
                      </Text>
                      <Input
                        placeholder="Cerca per titolo, cliente o creatore..."
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                      />
                    </div>

                    {/* Date Range */}
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        Periodo
                      </Text>
                      <RangePicker
                        showTime={false}
                        format="DD/MM/YYYY"
                        value={tblDateRange}
                        onChange={(rng) => {
                          if (!rng || rng.length !== 2 || !rng[0] || !rng[1]) return;
                          setTblDateRange([rng[0].startOf('day'), rng[1].endOf('day')]);
                        }}
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* Customer */}
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        Cliente
                      </Text>
                      <Select
                        allowClear
                        placeholder="Tutti i clienti"
                        style={{ width: '100%' }}
                        value={tblCustomer}
                        onChange={setTblCustomer}
                        options={customerOptions}
                        showSearch
                        optionFilterProp="label"
                      />
                    </div>

                    {/* Creatore */}
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        Creatore
                      </Text>
                      <Select
                        allowClear
                        placeholder="Tutti i creatori"
                        style={{ width: '100%' }}
                        value={tblCreator}
                        onChange={setTblCreator}
                        options={allCreatorLabels.map(l => ({ label: l, value: l }))}
                        showSearch
                        optionFilterProp="label"
                      />
                    </div>

                  </div>
                )
              }
            ]}
          />

          {/* Enhanced Table */}
          <Card style={{ borderRadius: 12 }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 16,
            }}>
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  Elenco consulenze
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {tableData.length} risultati {tableTotal !== tableData.length && `di ${tableTotal} totali`}
                </Text>
              </div>
              
            </div>

            <Table<ActivityDTO>
              rowKey="id"
              size="middle"
              dataSource={tableData}
              columns={columns}
              pagination={{
                current: tablePage,
                pageSize: tablePageSize,
                total: tableTotal,
                pageSizeOptions: [8, 10, 20, 50, 100],
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} di ${total} elementi`,
                onChange: (page, pageSize) => {
                  setTablePage(page);
                  setTablePageSize(pageSize);
                },
              }}
              onRow={(rec) => ({
                onClick: () => openEdit(rec.id),
                style: { cursor: 'pointer' },
              })}
              scroll={{ x: 800 }}
            />
          </Card>
        </>
      )}

      {/* Drawers */}
      <Drawer
        open={dayDrawerOpen}
        onClose={closeDayDrawer}
        width={420}
        title={drawerDate ? drawerDate.format('dddd DD MMMM YYYY') : 'Appuntamenti'}
        destroyOnClose
        extra={drawerDate ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              if (drawerDate) {
                openCreateForDate(drawerDate);
              }
            }}
          >
            Nuova consulenza
          </Button>
        ) : null}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {selectedDayEvents.length ? (
            selectedDayEvents.map(renderEventCard)
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#8c8c8c',
                border: '2px dashed #f0f0f0',
                borderRadius: 8,
              }}
            >
              Nessun appuntamento per questo giorno
            </div>
          )}
        </div>
      </Drawer>

      <CreateActivityDrawer
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setPresetStart(undefined);
          setPresetEnd(undefined);
        }}
        onCreated={() => {
          setCreateOpen(false);
          setPresetStart(undefined);
          setPresetEnd(undefined);
          refreshData();
          message.success('Consulenza creata con successo');
        }}
        forceType="CONSULTANCY"
        lockType
        presetAppointment={{
          startAt: presetStart,
          endAt: presetEnd,
        }}
      />

      <CreateActivityDrawer
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditId(undefined);
        }}
        onUpdated={() => {
          setEditOpen(false);
          setEditId(undefined);
          refreshData();
          message.success('Consulenza aggiornata con successo');
        }}
        mode="edit"
        activityId={editId}
        lockType
      />
    </StandardPage>
  );
};

export default ConsultingPage;
